import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertTriangle, Pencil, ImageIcon, Palette, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { get, patch, getErrorMessage, api } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import type { Society } from '@hooddesk/shared-types';
import type { SocietyParkingModel } from './_parking-types';
import { PARKING_MODEL_LABEL } from './_parking-types';

const UpdateSchema = z.object({
  name: z.string().min(2).max(160),
  city: z.string().max(80).optional().or(z.literal('')),
  state: z.string().max(80).optional().or(z.literal('')),
  address_line1: z.string().max(200).optional().or(z.literal('')),
  address_line2: z.string().max(200).optional().or(z.literal('')),
  postal_code: z.string().max(20).optional().or(z.literal('')),
  timezone: z.string().max(64),
  visitor_photo_retention_days: z.coerce.number().int().min(1).max(3650),
  feature_face_recog_enabled: z.boolean().default(false),
  feature_anpr_enabled: z.boolean().default(false),
  feature_gate_photo_mandatory: z.boolean().default(false),
  feature_family_can_approve: z.boolean().default(true),
  sos_broadcast_scope: z.enum(['guards_admins', 'plus_block', 'plus_society']).default('guards_admins')
});
type UpdateForm = z.infer<typeof UpdateSchema>;

type SocietyWithFlags = Society & {
  address_line1?: string | null;
  address_line2?: string | null;
  postal_code?: string | null;
  feature_gate_photo_mandatory?: boolean;
  feature_family_can_approve?: boolean;
  sos_broadcast_scope?: 'guards_admins' | 'plus_block' | 'plus_society';
  // Catalog-driven: tenants can be assigned any active PlanTier code.
  plan?: string;
  plan_status?: 'active' | 'past_due' | 'cancelled';
  plan_started_at?: string | null;
  plan_renews_at?: string | null;
  plan_price_inr_per_month?: number | null;
  plan_seats_units?: number | null;
  // White-label branding overrides (V0.4+). When null/undefined the admin
  // shell + login screen fall back to the default HoodDesk brand.
  brand_logo_url?: string | null;
  brand_logo_storage_key?: string | null;
  brand_primary_color?: string | null;
  brand_welcome_copy?: string | null;
  // Public-facing contact (V0.4+) — shown to residents on overdue banners + receipts.
  contact_phone?: string | null;
  contact_email?: string | null;
  // Parking policy (V0.3 — see _parking-types.SocietyParkingPolicy).
  parking_model?: SocietyParkingModel;
  parking_first_slot_in_maintenance?: boolean;
  parking_allow_owner_rental?: boolean;
  parking_society_cut_pct?: number;
  parking_rental_notice_days?: number;
  parking_max_slots_per_unit?: number;
  parking_two_wheeler_no_charge?: boolean;
  parking_visitor_max_hours?: number;
  parking_ev_default_kwh_rate_inr?: number;
  // Billing & GST (Phase 3 Charges).
  gst_registration_no?: string | null;
  receipt_number_prefix?: string | null;
  receipt_starting_number?: number | null;
  // Collections / Defaulters policy (V0.3+).
  collection_auto_reminder_enabled?: boolean;
  collection_reminder_day_offsets?: string | null;
  collection_reminder_default_channel?: 'email' | 'sms' | 'whatsapp' | 'app_notification';
};

const HEX_COLOR_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const BRANDING_LOGO_MAX_BYTES = 5 * 1024 * 1024; // 5 MB — keeps uploads snappy.
const BRANDING_LOGO_MIME = new Set(['image/jpeg', 'image/png', 'image/webp']);

const BrandingSchema = z.object({
  brand_primary_color: z
    .string()
    .regex(HEX_COLOR_RE, 'Use a hex color like #0d3b66')
    .or(z.literal(''))
    .optional(),
  brand_welcome_copy: z.string().max(500, 'Max 500 characters').optional().or(z.literal(''))
});
type BrandingForm = z.infer<typeof BrandingSchema>;

// Catalog-safe badge variant — unknown custom tier codes fall back to 'muted'.
function getPlanBadgeVariant(code: string): 'muted' | 'info' | 'ok' | 'warn' {
  const KNOWN: Record<string, 'muted' | 'info' | 'ok' | 'warn'> = {
    free: 'muted',
    lite: 'info',
    pro: 'ok',
    enterprise: 'warn'
  };
  return KNOWN[code] ?? 'muted';
}

// Active plan-tiers list — any-auth endpoint, society admin can read it to
// resolve a friendly label for the current tier code.
interface PlanTierActiveRow {
  code: string;
  label: string;
  description: string | null;
  price_inr_per_month: number;
  seats_cap_units: number | null;
}
interface PlanTierActiveResponse {
  items: PlanTierActiveRow[];
}

function capitalize(s: string): string {
  return s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1);
}

const SOS_SCOPE_OPTIONS: ReadonlyArray<{
  value: 'guards_admins' | 'plus_block' | 'plus_society';
  label: string;
  helper: string;
}> = [
  { value: 'guards_admins', label: 'Guards and admins only', helper: 'Privacy-first. Default.' },
  { value: 'plus_block', label: 'Plus residents in the same block', helper: 'Neighbours may help respond.' },
  { value: 'plus_society', label: 'Plus all residents in the society', helper: 'Widest reach. Low privacy.' }
];

const TIMEZONES = [
  'Asia/Kolkata',
  'Asia/Dubai',
  'Asia/Singapore',
  'Asia/Karachi',
  'Asia/Dhaka',
  'Asia/Tokyo',
  'Europe/London',
  'Europe/Berlin',
  'America/New_York',
  'America/Los_Angeles',
  'UTC'
];

export function SettingsPage(): JSX.Element {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['society', 'me'],
    queryFn: () => get<Society>('/society/me')
  });

  // Plans-as-data: resolve the assigned tier code → friendly label. Endpoint
  // is any-auth (society admins can read). On failure we fall back to a
  // capitalized version of the code, so display never breaks.
  const tiersQuery = useQuery({
    queryKey: ['plan-tiers', 'active'],
    queryFn: () => get<PlanTierActiveResponse>('/superadmin/plan-tiers/active'),
    retry: false,
    staleTime: 5 * 60 * 1000
  });

  const form = useForm<UpdateForm>({
    resolver: zodResolver(UpdateSchema),
    defaultValues: {
      name: '',
      city: '',
      state: '',
      address_line1: '',
      address_line2: '',
      postal_code: '',
      timezone: 'Asia/Kolkata',
      visitor_photo_retention_days: 30,
      feature_face_recog_enabled: false,
      feature_anpr_enabled: false,
      feature_gate_photo_mandatory: false,
      feature_family_can_approve: true,
      sos_broadcast_scope: 'guards_admins'
    }
  });

  // Hydrate form when society data loads or dialog opens
  useEffect(() => {
    if (open && query.data) {
      const data = query.data as SocietyWithFlags;
      form.reset({
        name: data.name,
        city: data.city ?? '',
        state: data.state ?? '',
        address_line1: data.address_line1 ?? '',
        address_line2: data.address_line2 ?? '',
        postal_code: data.postal_code ?? '',
        timezone: data.timezone,
        visitor_photo_retention_days: data.visitor_photo_retention_days,
        feature_face_recog_enabled: data.feature_face_recog_enabled,
        feature_anpr_enabled: data.feature_anpr_enabled,
        feature_gate_photo_mandatory: data.feature_gate_photo_mandatory ?? false,
        feature_family_can_approve: data.feature_family_can_approve ?? true,
        sos_broadcast_scope: data.sos_broadcast_scope ?? 'guards_admins'
      });
    }
  }, [open, query.data, form]);

  const update = useMutation({
    mutationFn: (values: UpdateForm) =>
      patch<Society, UpdateForm>('/society/me', {
        ...values,
        city: values.city || undefined,
        state: values.state || undefined,
        address_line1: values.address_line1 || undefined,
        address_line2: values.address_line2 || undefined,
        postal_code: values.postal_code || undefined
      }),
    onSuccess: () => {
      toast.success('Society profile updated');
      void queryClient.invalidateQueries({ queryKey: ['society'] });
      setOpen(false);
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const editDialog = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Pencil className="h-4 w-4" /> Edit profile
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit society profile</DialogTitle>
          <DialogDescription>
            Updates the public-facing society details. Jurisdiction tag is locked (compliance).
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit((v) => update.mutate(v))} className="flex flex-col gap-vt-4">
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="name">Society name</Label>
            <Input
              id="name"
              autoFocus
              aria-invalid={Boolean(form.formState.errors.name) || undefined}
              {...form.register('name')}
            />
            {form.formState.errors.name ? (
              <p className="text-vt-12 text-vt-alert" role="alert">{form.formState.errors.name.message}</p>
            ) : null}
          </div>
          <div className="grid grid-cols-2 gap-vt-3">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="city">City</Label>
              <Input id="city" {...form.register('city')} />
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="state">State / Province</Label>
              <Input id="state" {...form.register('state')} />
            </div>
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="address_line1">Address line 1</Label>
            <Input id="address_line1" {...form.register('address_line1')} />
          </div>
          <div className="grid grid-cols-2 gap-vt-3">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="address_line2">Address line 2</Label>
              <Input id="address_line2" {...form.register('address_line2')} />
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="postal_code">Postal code</Label>
              <Input id="postal_code" {...form.register('postal_code')} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-vt-3">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="timezone">Timezone</Label>
              <Select id="timezone" {...form.register('timezone')}>
                {TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="visitor_photo_retention_days">Photo retention (days)</Label>
              <Input
                id="visitor_photo_retention_days"
                type="number"
                min={1}
                max={3650}
                {...form.register('visitor_photo_retention_days')}
              />
            </div>
          </div>
          <fieldset className="flex flex-col gap-vt-2 rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3">
            <legend className="px-vt-1 text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
              AI feature toggles
            </legend>
            <label className="flex items-center gap-vt-2 text-vt-13 text-vt-text">
              <input type="checkbox" {...form.register('feature_face_recog_enabled')} className="h-4 w-4" />
              Face recognition (jurisdiction-gated; check legal before enabling)
            </label>
            <label className="flex items-center gap-vt-2 text-vt-13 text-vt-text">
              <input type="checkbox" {...form.register('feature_anpr_enabled')} className="h-4 w-4" />
              ANPR (number-plate recognition)
            </label>
          </fieldset>
          <fieldset className="flex flex-col gap-vt-3 rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3">
            <legend className="px-vt-1 text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
              Visitor &amp; safety
            </legend>
            <div className="flex flex-col gap-vt-1">
              <label className="flex items-center gap-vt-2 text-vt-13 text-vt-text">
                <input
                  type="checkbox"
                  {...form.register('feature_gate_photo_mandatory')}
                  className="h-4 w-4"
                />
                Require gate photo on check-in
              </label>
              <p className="pl-vt-6 text-vt-12 text-vt-text-muted">
                Guards must capture a visitor photo before allowing entry.
              </p>
            </div>
            <div className="flex flex-col gap-vt-1">
              <label className="flex items-center gap-vt-2 text-vt-13 text-vt-text">
                <input
                  type="checkbox"
                  {...form.register('feature_family_can_approve')}
                  className="h-4 w-4"
                />
                Allow family members to approve visitors
              </label>
              <p className="pl-vt-6 text-vt-12 text-vt-text-muted">
                When off, only the primary owner can approve.
              </p>
            </div>
            <div className="flex flex-col gap-vt-2">
              <span className="text-vt-13 font-medium text-vt-text">SOS broadcast scope</span>
              <div className="flex flex-col gap-vt-2">
                {SOS_SCOPE_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className="flex items-start gap-vt-2 rounded-vt-sm border border-vt-border bg-vt-surface px-vt-3 py-vt-2 text-vt-13 text-vt-text hover:bg-vt-surface-2"
                  >
                    <input
                      type="radio"
                      value={opt.value}
                      className="mt-vt-1 h-4 w-4"
                      {...form.register('sos_broadcast_scope')}
                    />
                    <span className="flex flex-col">
                      <span className="font-medium">{opt.label}</span>
                      <span className="text-vt-12 text-vt-text-muted">{opt.helper}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </fieldset>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={update.isPending} loadingText="Saving…">
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  return (
    <>
      <PageHeader
        title="Settings"
        description="Society profile, branding, retention policies, and feature toggles."
        actions={editDialog}
      />
      <div className="mx-auto w-full max-w-[1400px] space-y-vt-6 px-vt-6 pb-vt-8 pt-vt-4">
        {/* Personal account controls — notification preferences + active
            sessions ("My devices"). Self-service for the signed-in admin /
            operator; independent of the society-level settings below. */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-vt-3">
              <div>
                <CardTitle>Your account</CardTitle>
                <CardDescription>
                  Control how HoodDesk reaches you and which devices are signed in.
                </CardDescription>
              </div>
              <Badge variant="info" className="uppercase tracking-wide">Personal</Badge>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col gap-vt-2 sm:flex-row sm:items-center sm:justify-start">
            <a
              href="/me/preferences"
              className="inline-flex items-center justify-center rounded-vt-sm border border-vt-border bg-vt-surface px-vt-3 py-vt-2 text-vt-13 font-medium text-vt-text hover:bg-vt-surface-2"
            >
              Notification preferences
            </a>
            <a
              href="/me/sessions"
              className="inline-flex items-center justify-center rounded-vt-sm border border-vt-border bg-vt-surface px-vt-3 py-vt-2 text-vt-13 font-medium text-vt-text hover:bg-vt-surface-2"
            >
              Sessions &amp; devices
            </a>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-vt-3">
              <div>
                <CardTitle>Society profile</CardTitle>
                <CardDescription>Click "Edit profile" to update name, address, timezone, retention or AI toggles.</CardDescription>
              </div>
              <Badge variant="ok" className="uppercase tracking-wide">Editable</Badge>
            </div>
          </CardHeader>
          <CardContent>
            {query.isLoading ? (
              <div className="grid gap-vt-3 md:grid-cols-2">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : query.isError ? (
              <EmptyState
                icon={<AlertTriangle className="h-6 w-6" strokeWidth={1.75} />}
                title="Could not load society"
                description={(query.error as Error)?.message ?? 'API may not yet expose /society/me — check API route mapping.'}
                action={
                  <Button variant="secondary" onClick={() => void query.refetch()}>
                    Try again
                  </Button>
                }
              />
            ) : query.data ? (
              <dl className="grid grid-cols-1 gap-vt-4 md:grid-cols-2">
                <Field label="Name" value={query.data.name} />
                <Field label="Country" value={query.data.country} />
                <Field label="State" value={query.data.state ?? '—'} />
                <Field label="City" value={query.data.city ?? '—'} />
                <Field label="Timezone" value={query.data.timezone} />
                <Field label="Jurisdiction" value={query.data.jurisdiction_tag} />
                <Field label="Photo retention" value={`${query.data.visitor_photo_retention_days} days`} />
                <Field
                  label="Features"
                  value={
                    <span className="flex flex-wrap gap-vt-1">
                      {query.data.feature_face_recog_enabled ? <Badge variant="ok">Face recognition</Badge> : null}
                      {query.data.feature_anpr_enabled ? <Badge variant="ok">ANPR</Badge> : null}
                      {!query.data.feature_face_recog_enabled && !query.data.feature_anpr_enabled ? (
                        <Badge variant="muted">None enabled</Badge>
                      ) : null}
                    </span>
                  }
                />
              </dl>
            ) : null}
          </CardContent>
        </Card>

        {/* Branding — per-tenant white-label. Admin uploads a logo + sets a
            brand color + a custom welcome copy that appears on the admin
            login screen. All fields are optional; when blank, the default
            HoodDesk brand is shown. */}
        {query.data ? <BrandingSection society={query.data as SocietyWithFlags} /> : null}

        {/* Billing & GST — Phase 3 Charges. Admin sets GSTIN + receipt
            numbering policy. Receipt number is generated as
            `<prefix>/<YYYY>/<MM>/<seqNNNN>` starting at receipt_starting_number. */}
        {query.data ? <BillingGstSection society={query.data as SocietyWithFlags} /> : null}

        {/* Collection policy — auto-reminder + day offsets + default channel.
            Backed by /v1/society/me/collection-policy. */}
        {query.data ? <CollectionPolicySection society={query.data as SocietyWithFlags} /> : null}

        {/* Parking policy — V0.3. Governs society-wide parking behaviour:
            ownership model, rental cuts, notice periods, visitor limits, and
            EV default rate. */}
        {query.data ? <ParkingPolicySection society={query.data as SocietyWithFlags} /> : null}

        {/* Subscription — read-only. Plan is managed by the SaaS super-admin
            (Smart Desizns / HoodDesk team). Society admin sees the current
            tier + renewal so they know what scope they're entitled to. */}
        {query.data && (query.data as SocietyWithFlags).plan ? (
          <Card>
            <CardContent className="space-y-vt-3 p-vt-4">
              <div className="flex items-center justify-between">
                <h2 className="text-vt-14 font-semibold text-vt-text">Subscription</h2>
                <span className="text-vt-12 text-vt-text-soft">Managed by HoodDesk</span>
              </div>
              {(() => {
                const s = query.data as SocietyWithFlags;
                const plan = s.plan ?? 'free';
                const planStatus = s.plan_status ?? 'active';
                const variant = getPlanBadgeVariant(plan);
                const catalogRow = tiersQuery.data?.items.find((t) => t.code === plan);
                const planLabel = catalogRow ? catalogRow.label : capitalize(plan);
                return (
                  <dl className="grid grid-cols-1 gap-vt-4 md:grid-cols-3">
                    <Field
                      label="Plan"
                      value={
                        <span className="flex items-center gap-vt-2">
                          <Badge variant={variant} className="capitalize">
                            {planLabel}
                          </Badge>
                          <Badge
                            variant={
                              planStatus === 'active'
                                ? 'ok'
                                : planStatus === 'past_due'
                                  ? 'alert'
                                  : 'muted'
                            }
                            className="capitalize"
                          >
                            {planStatus.replace('_', ' ')}
                          </Badge>
                        </span>
                      }
                    />
                    <Field
                      label="Price"
                      value={
                        s.plan_price_inr_per_month === null || s.plan_price_inr_per_month === undefined
                          ? 'Contact us'
                          : s.plan_price_inr_per_month === 0
                            ? 'Free'
                            : `₹${s.plan_price_inr_per_month.toLocaleString('en-IN')}/month`
                      }
                    />
                    <Field
                      label="Seats (units cap)"
                      value={
                        s.plan_seats_units === null || s.plan_seats_units === undefined
                          ? 'Unlimited'
                          : `${s.plan_seats_units} units`
                      }
                    />
                    <Field
                      label="Started"
                      value={
                        s.plan_started_at
                          ? new Date(s.plan_started_at).toLocaleDateString('en-IN')
                          : '—'
                      }
                    />
                    <Field
                      label="Renews"
                      value={
                        s.plan_renews_at
                          ? new Date(s.plan_renews_at).toLocaleDateString('en-IN')
                          : '—'
                      }
                    />
                    <Field
                      label="Need a change?"
                      value={
                        <a
                          href="mailto:billing@hooddesk.com?subject=Plan%20change%20request"
                          className="text-vt-primary hover:underline"
                        >
                          Contact billing
                        </a>
                      }
                    />
                  </dl>
                );
              })()}
            </CardContent>
          </Card>
        ) : null}

      </div>
    </>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }): JSX.Element {
  return (
    <div className="rounded-vt-sm bg-vt-surface-2/40 px-vt-3 py-vt-2">
      <dt className="text-[10px] uppercase tracking-[0.08em] text-vt-text-soft">{label}</dt>
      <dd className="mt-vt-1 text-vt-14 font-medium text-vt-text">{value}</dd>
    </div>
  );
}

/**
 * BrandingSection — white-label controls. Manages three things:
 *
 *   1. Logo upload (multipart → POST /v1/society/me/logo, mirrors the Staff
 *      photo pattern). The preview is rendered via the auth-gated GET stream
 *      and a blob-URL because <img src> tags can't carry Bearer headers.
 *   2. Brand primary color (hex). Stored on `Society.brand_primary_color`.
 *   3. Welcome copy (≤ 500 chars). Shown on the login screen above the form.
 *
 * Saves go through PATCH /v1/society/me — same endpoint as the profile dialog
 * — so the React-Query cache invalidation cascades the same way and the
 * AppShell/login screen re-render with the new brand on the next paint.
 */
function BrandingSection({ society }: { society: SocietyWithFlags }): JSX.Element {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<boolean>(false);

  // Fetch the brand logo as an auth-gated blob and surface as an object URL.
  // Re-runs whenever brand_logo_url changes (the ts= query param busts on
  // every fresh upload, so this hooks naturally cache-busts).
  useEffect(() => {
    setPreviewError(false);
    if (!society.brand_logo_url) {
      setPreviewUrl(null);
      return;
    }
    let revoked = false;
    let createdUrl: string | null = null;
    api
      .get<Blob>('/society/me/logo', { responseType: 'blob' })
      .then((res) => {
        if (revoked) return;
        createdUrl = URL.createObjectURL(res.data);
        setPreviewUrl(createdUrl);
      })
      .catch(() => {
        if (!revoked) {
          setPreviewUrl(null);
          setPreviewError(true);
        }
      });
    return () => {
      revoked = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [society.brand_logo_url]);

  const brandingForm = useForm<BrandingForm>({
    resolver: zodResolver(BrandingSchema),
    defaultValues: {
      brand_primary_color: society.brand_primary_color ?? '',
      brand_welcome_copy: society.brand_welcome_copy ?? ''
    }
  });

  // Keep the form in sync if the parent React-Query refetch updates society.
  useEffect(() => {
    brandingForm.reset({
      brand_primary_color: society.brand_primary_color ?? '',
      brand_welcome_copy: society.brand_welcome_copy ?? ''
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [society.brand_primary_color, society.brand_welcome_copy]);

  const colorValue = brandingForm.watch('brand_primary_color') ?? '';
  const colorIsValid = !colorValue || HEX_COLOR_RE.test(colorValue);

  const saveBranding = useMutation({
    mutationFn: (values: BrandingForm) =>
      patch<Society, { brand_primary_color: string | null; brand_welcome_copy: string | null }>(
        '/society/me',
        {
          brand_primary_color: values.brand_primary_color?.trim() ? values.brand_primary_color.trim() : null,
          brand_welcome_copy: values.brand_welcome_copy?.trim() ? values.brand_welcome_copy.trim() : null
        }
      ),
    onSuccess: () => {
      toast.success('Branding saved');
      void queryClient.invalidateQueries({ queryKey: ['society'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const uploadLogo = useMutation({
    mutationFn: async (file: File) => {
      if (!BRANDING_LOGO_MIME.has(file.type)) {
        throw new Error('Logo must be JPEG, PNG, or WebP');
      }
      if (file.size > BRANDING_LOGO_MAX_BYTES) {
        throw new Error('Logo must be 5 MB or smaller');
      }
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post<{ logo_url: string; brand_logo_url: string }>(
        '/society/me/logo',
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return res.data;
    },
    onSuccess: () => {
      toast.success('Logo uploaded');
      void queryClient.invalidateQueries({ queryKey: ['society'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadLogo.mutate(file);
    // Allow re-selecting the same file.
    e.target.value = '';
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-vt-3">
          <div>
            <CardTitle>Branding</CardTitle>
            <CardDescription>
              White-label your admin shell and login screen. Optional — leave blank to use the default HoodDesk brand.
            </CardDescription>
          </div>
          <Badge variant="muted" className="uppercase tracking-wide">
            White-label
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={brandingForm.handleSubmit((v) => saveBranding.mutate(v))}
          className="grid grid-cols-1 gap-vt-6 md:grid-cols-2"
        >
          {/* Logo */}
          <div className="flex flex-col gap-vt-3">
            <Label>Society logo</Label>
            <div className="flex items-center gap-vt-4 rounded-vt-md border border-vt-border bg-vt-surface-2/40 p-vt-4">
              <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-vt-md border border-vt-border bg-vt-surface">
                {previewUrl ? (
                  <img src={previewUrl} alt="Society logo" className="h-full w-full object-contain" />
                ) : (
                  <ImageIcon
                    className="h-8 w-8 text-vt-text-soft"
                    strokeWidth={1.5}
                    aria-hidden="true"
                  />
                )}
              </div>
              <div className="flex min-w-0 flex-1 flex-col gap-vt-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={onPickFile}
                  className="hidden"
                  aria-label="Upload society logo"
                />
                <div className="flex flex-wrap gap-vt-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => fileInputRef.current?.click()}
                    loading={uploadLogo.isPending}
                    loadingText="Uploading…"
                  >
                    <Upload className="h-3.5 w-3.5" />
                    {society.brand_logo_url ? 'Replace logo' : 'Upload logo'}
                  </Button>
                </div>
                <p className="text-vt-12 text-vt-text-soft">
                  PNG, JPEG, or WebP. Max 5 MB. Square or wide logos work best.
                </p>
                {previewError ? (
                  <p className="text-vt-12 text-vt-alert" role="alert">
                    Could not load the current logo preview.
                  </p>
                ) : null}
              </div>
            </div>
          </div>

          {/* Color + copy */}
          <div className="flex flex-col gap-vt-4">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="brand_primary_color">Brand color</Label>
              <div className="flex items-center gap-vt-2">
                <input
                  id="brand_primary_color_picker"
                  type="color"
                  value={colorIsValid && colorValue ? colorValue : '#0d3b66'}
                  onChange={(e) =>
                    brandingForm.setValue('brand_primary_color', e.target.value, { shouldDirty: true })
                  }
                  className="h-9 w-12 shrink-0 cursor-pointer rounded-vt-sm border border-vt-border bg-vt-surface"
                  aria-label="Pick brand color"
                />
                <Input
                  id="brand_primary_color"
                  placeholder="#0d3b66"
                  className="font-mono"
                  {...brandingForm.register('brand_primary_color')}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    brandingForm.setValue('brand_primary_color', '', { shouldDirty: true })
                  }
                  title="Clear brand color"
                  aria-label="Clear brand color"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
              {brandingForm.formState.errors.brand_primary_color ? (
                <p className="text-vt-12 text-vt-alert" role="alert">
                  {brandingForm.formState.errors.brand_primary_color.message}
                </p>
              ) : (
                <p className="text-vt-12 text-vt-text-soft">
                  Overrides the default HoodDesk blue on the admin shell + ID-card header.
                </p>
              )}
            </div>

            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="brand_welcome_copy">Welcome copy (login screen)</Label>
              <textarea
                id="brand_welcome_copy"
                rows={3}
                maxLength={500}
                className="rounded-vt-sm border border-vt-border bg-vt-surface px-vt-3 py-vt-2 text-vt-14 text-vt-text outline-none focus-visible:ring-2 focus-visible:ring-vt-primary-400"
                placeholder="Welcome to Greenwood Heights — sign in with your registered phone number."
                {...brandingForm.register('brand_welcome_copy')}
              />
              {brandingForm.formState.errors.brand_welcome_copy ? (
                <p className="text-vt-12 text-vt-alert" role="alert">
                  {brandingForm.formState.errors.brand_welcome_copy.message}
                </p>
              ) : (
                <p className="text-vt-12 text-vt-text-soft">
                  Shown above the phone-number field on the admin login. Max 500 characters.
                </p>
              )}
            </div>
          </div>

          <div className="md:col-span-2 flex items-center justify-between gap-vt-3 border-t border-vt-border pt-vt-4">
            <div className="flex items-center gap-vt-2 text-vt-12 text-vt-text-soft">
              <Palette className="h-3.5 w-3.5" strokeWidth={1.75} />
              <span>Changes apply on next page load.</span>
            </div>
            <Button
              type="submit"
              loading={saveBranding.isPending}
              loadingText="Saving…"
              disabled={!brandingForm.formState.isDirty}
            >
              Save branding
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

/**
 * ParkingPolicySection — society-wide parking policy. Backed by the parking_*
 * fields on the society row. PATCH /v1/society/me accepts the same keys.
 *
 * Toggles below are intentionally NOT shown for the 'all_purchased' model where
 * a few values (notice days, society cut, etc.) don't apply — fewer surfaces
 * to maintain mentally and the server enforces semantics anyway.
 */
const ParkingPolicySchema = z.object({
  parking_model: z.enum(['all_purchased', 'all_society_rental', 'hybrid']).default('hybrid'),
  parking_first_slot_in_maintenance: z.boolean().default(true),
  parking_allow_owner_rental: z.boolean().default(true),
  parking_society_cut_pct: z.coerce.number().int().min(0).max(50).default(10),
  parking_rental_notice_days: z.coerce.number().int().min(0).max(365).default(30),
  parking_max_slots_per_unit: z.coerce.number().int().min(0).max(20).default(2),
  parking_two_wheeler_no_charge: z.boolean().default(false),
  parking_visitor_max_hours: z.coerce.number().int().min(0).max(168).default(24),
  parking_ev_default_kwh_rate_inr: z.coerce.number().min(0).max(1000).default(12)
});
type ParkingPolicyForm = z.infer<typeof ParkingPolicySchema>;

function ParkingPolicySection({ society }: { society: SocietyWithFlags }): JSX.Element {
  const queryClient = useQueryClient();
  const form = useForm<ParkingPolicyForm>({
    resolver: zodResolver(ParkingPolicySchema),
    defaultValues: {
      parking_model: society.parking_model ?? 'hybrid',
      parking_first_slot_in_maintenance: society.parking_first_slot_in_maintenance ?? true,
      parking_allow_owner_rental: society.parking_allow_owner_rental ?? true,
      parking_society_cut_pct: society.parking_society_cut_pct ?? 10,
      parking_rental_notice_days: society.parking_rental_notice_days ?? 30,
      parking_max_slots_per_unit: society.parking_max_slots_per_unit ?? 2,
      parking_two_wheeler_no_charge: society.parking_two_wheeler_no_charge ?? false,
      parking_visitor_max_hours: society.parking_visitor_max_hours ?? 24,
      parking_ev_default_kwh_rate_inr: society.parking_ev_default_kwh_rate_inr ?? 12
    }
  });

  // Keep form in sync with refetch.
  useEffect(() => {
    form.reset({
      parking_model: society.parking_model ?? 'hybrid',
      parking_first_slot_in_maintenance: society.parking_first_slot_in_maintenance ?? true,
      parking_allow_owner_rental: society.parking_allow_owner_rental ?? true,
      parking_society_cut_pct: society.parking_society_cut_pct ?? 10,
      parking_rental_notice_days: society.parking_rental_notice_days ?? 30,
      parking_max_slots_per_unit: society.parking_max_slots_per_unit ?? 2,
      parking_two_wheeler_no_charge: society.parking_two_wheeler_no_charge ?? false,
      parking_visitor_max_hours: society.parking_visitor_max_hours ?? 24,
      parking_ev_default_kwh_rate_inr: society.parking_ev_default_kwh_rate_inr ?? 12
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    society.parking_model,
    society.parking_first_slot_in_maintenance,
    society.parking_allow_owner_rental,
    society.parking_society_cut_pct,
    society.parking_rental_notice_days,
    society.parking_max_slots_per_unit,
    society.parking_two_wheeler_no_charge,
    society.parking_visitor_max_hours,
    society.parking_ev_default_kwh_rate_inr
  ]);

  const save = useMutation({
    mutationFn: (v: ParkingPolicyForm) => patch<Society, ParkingPolicyForm>('/society/me', v),
    onSuccess: () => {
      toast.success('Parking policy saved');
      void queryClient.invalidateQueries({ queryKey: ['society'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const model = form.watch('parking_model') as SocietyParkingModel;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-vt-3">
          <div>
            <CardTitle>Parking policy</CardTitle>
            <CardDescription>
              Ownership model, marketplace rules, visitor limits, and EV defaults.
            </CardDescription>
          </div>
          <Badge variant="info" className="uppercase tracking-wide">Parking</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={form.handleSubmit((v) => save.mutate(v))}
          className="flex flex-col gap-vt-5"
        >
          <fieldset className="flex flex-col gap-vt-2">
            <legend className="px-vt-1 pb-vt-1 text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
              Ownership model
            </legend>
            <div className="grid grid-cols-1 gap-vt-2 md:grid-cols-3">
              {(['all_purchased', 'all_society_rental', 'hybrid'] as SocietyParkingModel[]).map(
                (m) => (
                  <label
                    key={m}
                    className="flex cursor-pointer items-start gap-vt-2 rounded-vt-sm border border-vt-border bg-vt-surface px-vt-3 py-vt-2 text-vt-13 text-vt-text hover:bg-vt-surface-2"
                  >
                    <input
                      type="radio"
                      value={m}
                      className="mt-vt-1 h-4 w-4"
                      {...form.register('parking_model')}
                    />
                    <span className="flex flex-col">
                      <span className="font-medium">{PARKING_MODEL_LABEL[m]}</span>
                      <span className="text-vt-12 text-vt-text-muted">
                        {m === 'all_purchased'
                          ? 'Every slot is owned by a unit.'
                          : m === 'all_society_rental'
                            ? 'Society owns every slot, residents pay rent.'
                            : 'Mix of unit-owned and society-rental slots.'}
                      </span>
                    </span>
                  </label>
                )
              )}
            </div>
          </fieldset>

          <fieldset className="flex flex-col gap-vt-3 rounded-vt-md border border-vt-border bg-vt-surface-2/40 p-vt-3">
            <legend className="px-vt-1 text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
              Charges &amp; rentals
            </legend>
            <label className="flex items-center gap-vt-2 text-vt-13 text-vt-text">
              <input
                type="checkbox"
                {...form.register('parking_first_slot_in_maintenance')}
                className="h-4 w-4"
              />
              First slot per unit is included in maintenance (no extra charge)
            </label>
            <label className="flex items-center gap-vt-2 text-vt-13 text-vt-text">
              <input
                type="checkbox"
                {...form.register('parking_allow_owner_rental')}
                className="h-4 w-4"
                disabled={model === 'all_society_rental'}
              />
              Allow owners to rent slots to other residents
            </label>
            <label className="flex items-center gap-vt-2 text-vt-13 text-vt-text">
              <input
                type="checkbox"
                {...form.register('parking_two_wheeler_no_charge')}
                className="h-4 w-4"
              />
              Two-wheeler slots have no monthly charge
            </label>
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-3">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="pp-cut">Society cut on owner-rentals (%)</Label>
                <Input
                  id="pp-cut"
                  type="number"
                  min={0}
                  max={50}
                  {...form.register('parking_society_cut_pct')}
                />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="pp-notice">Rental notice period (days)</Label>
                <Input
                  id="pp-notice"
                  type="number"
                  min={0}
                  max={365}
                  {...form.register('parking_rental_notice_days')}
                />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="pp-max">Max slots per unit</Label>
                <Input
                  id="pp-max"
                  type="number"
                  min={0}
                  max={20}
                  {...form.register('parking_max_slots_per_unit')}
                />
              </div>
            </div>
          </fieldset>

          <fieldset className="flex flex-col gap-vt-3 rounded-vt-md border border-vt-border bg-vt-surface-2/40 p-vt-3">
            <legend className="px-vt-1 text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
              Visitors &amp; EV
            </legend>
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="pp-vmax">Visitor max parking (hours)</Label>
                <Input
                  id="pp-vmax"
                  type="number"
                  min={0}
                  max={168}
                  {...form.register('parking_visitor_max_hours')}
                />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="pp-evrate">EV default rate (₹/kWh)</Label>
                <Input
                  id="pp-evrate"
                  type="number"
                  min={0}
                  step="0.01"
                  {...form.register('parking_ev_default_kwh_rate_inr')}
                />
              </div>
            </div>
          </fieldset>

          <div className="flex items-center justify-end gap-vt-2 border-t border-vt-border pt-vt-3">
            <Button
              type="submit"
              loading={save.isPending}
              loadingText="Saving…"
              disabled={!form.formState.isDirty}
            >
              Save parking policy
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

/**
 * BillingGstSection — Phase 3 Charges. Admin sets:
 *   - GSTIN (society's GST registration number, printed on receipt PDF)
 *   - Receipt number prefix (e.g. "SOC" — default applied server-side when empty)
 *   - Receipt starting number (defaults to 1)
 *
 * The receipt_number is generated as `<prefix>/<YYYY>/<MM>/<seqNNNN>` per
 * society. Changing the prefix only affects subsequent receipts.
 */
const GstinRe = /^[0-9A-Z]{15}$/;
const BillingGstSchema = z.object({
  gst_registration_no: z
    .string()
    .trim()
    .regex(GstinRe, '15-character GSTIN (alphanumeric uppercase)')
    .or(z.literal(''))
    .optional(),
  receipt_number_prefix: z.string().trim().max(20).optional().or(z.literal('')),
  receipt_starting_number: z.coerce.number().int().min(1).max(1_000_000).optional()
});
type BillingGstForm = z.infer<typeof BillingGstSchema>;

function BillingGstSection({ society }: { society: SocietyWithFlags }): JSX.Element {
  const qc = useQueryClient();

  const form = useForm<BillingGstForm>({
    resolver: zodResolver(BillingGstSchema),
    defaultValues: {
      gst_registration_no: society.gst_registration_no ?? '',
      receipt_number_prefix: society.receipt_number_prefix ?? '',
      receipt_starting_number: society.receipt_starting_number ?? 1
    },
    mode: 'onChange'
  });
  useEffect(() => {
    form.reset({
      gst_registration_no: society.gst_registration_no ?? '',
      receipt_number_prefix: society.receipt_number_prefix ?? '',
      receipt_starting_number: society.receipt_starting_number ?? 1
    });
  }, [
    society.gst_registration_no,
    society.receipt_number_prefix,
    society.receipt_starting_number
  ]);

  const save = useMutation({
    mutationFn: (v: BillingGstForm) => {
      const payload: Record<string, unknown> = {};
      const gstin = (v.gst_registration_no ?? '').trim();
      payload.gst_registration_no = gstin === '' ? null : gstin;
      const prefix = (v.receipt_number_prefix ?? '').trim();
      payload.receipt_number_prefix = prefix === '' ? null : prefix;
      payload.receipt_starting_number = v.receipt_starting_number ?? 1;
      return patch<SocietyWithFlags>('/society/me', payload);
    },
    onSuccess: () => {
      toast.success('Billing & GST settings saved');
      void qc.invalidateQueries({ queryKey: ['society', 'me'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Billing & GST</CardTitle>
        <CardDescription>
          GSTIN appears on the receipt PDF. Receipt numbers are generated as
          {' '}<code>prefix/YYYY/MM/seq</code> (default prefix "SOC").
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={form.handleSubmit((v) => save.mutate(v))}
          className="flex flex-col gap-vt-3"
          noValidate
        >
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-3">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="bg-gstin">GSTIN (optional)</Label>
              <Input
                id="bg-gstin"
                maxLength={15}
                placeholder="22AAAAA0000A1Z5"
                {...form.register('gst_registration_no')}
              />
              {form.formState.errors.gst_registration_no ? (
                <p className="text-vt-12 text-vt-alert">
                  {form.formState.errors.gst_registration_no.message}
                </p>
              ) : null}
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="bg-prefix">Receipt prefix</Label>
              <Input
                id="bg-prefix"
                maxLength={20}
                placeholder="SOC"
                {...form.register('receipt_number_prefix')}
              />
              <p className="text-vt-11 text-vt-text-muted">Defaults to "SOC" when empty.</p>
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="bg-seq">Starting receipt #</Label>
              <Input
                id="bg-seq"
                type="number"
                min={1}
                max={1_000_000}
                {...form.register('receipt_starting_number')}
              />
              <p className="text-vt-11 text-vt-text-muted">
                Used only for the very first receipt. Subsequent receipts auto-increment.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-end gap-vt-2 border-t border-vt-border pt-vt-3">
            <Button
              type="submit"
              loading={save.isPending}
              loadingText="Saving…"
              disabled={!form.formState.isDirty}
            >
              Save Billing & GST
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

/**
 * CollectionPolicySection — defaulter automation. Backed by
 * `Society.collection_auto_reminder_enabled`, `collection_reminder_day_offsets`
 * (CSV of int days after due_date), and `collection_reminder_default_channel`.
 *
 * PATCH /v1/society/me/collection-policy validates + normalises the offsets
 * string (trims, sorts asc) before persisting; we just send raw input.
 */
const CollectionPolicySchema = z.object({
  auto_reminder_enabled: z.boolean(),
  reminder_day_offsets: z
    .string()
    .trim()
    .max(60)
    .optional()
    .refine(
      (v) =>
        v === undefined ||
        v === '' ||
        /^\d+(\s*,\s*\d+)*$/.test(v),
      'Use comma-separated positive integers (e.g. "3,7,14,30")'
    ),
  default_channel: z.enum(['email', 'sms', 'whatsapp', 'app_notification'])
});
type CollectionPolicyForm = z.infer<typeof CollectionPolicySchema>;

function CollectionPolicySection({ society }: { society: SocietyWithFlags }): JSX.Element {
  const qc = useQueryClient();
  const form = useForm<CollectionPolicyForm>({
    resolver: zodResolver(CollectionPolicySchema),
    defaultValues: {
      auto_reminder_enabled: society.collection_auto_reminder_enabled ?? false,
      reminder_day_offsets: society.collection_reminder_day_offsets ?? '',
      default_channel: society.collection_reminder_default_channel ?? 'email'
    }
  });
  useEffect(() => {
    form.reset({
      auto_reminder_enabled: society.collection_auto_reminder_enabled ?? false,
      reminder_day_offsets: society.collection_reminder_day_offsets ?? '',
      default_channel: society.collection_reminder_default_channel ?? 'email'
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    society.collection_auto_reminder_enabled,
    society.collection_reminder_day_offsets,
    society.collection_reminder_default_channel
  ]);

  const save = useMutation({
    mutationFn: (v: CollectionPolicyForm) =>
      patch('/society/me/collection-policy', {
        auto_reminder_enabled: v.auto_reminder_enabled,
        reminder_day_offsets: v.reminder_day_offsets?.trim() ? v.reminder_day_offsets.trim() : null,
        default_channel: v.default_channel
      }),
    onSuccess: () => {
      toast.success('Collection policy saved');
      void qc.invalidateQueries({ queryKey: ['society', 'me'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-vt-3">
          <div>
            <CardTitle>Collection policy</CardTitle>
            <CardDescription>
              Auto-reminder rules for defaulters. Day offsets are days after the bill's due date when a
              reminder should be queued. Stub today — wiring up to SMS/WhatsApp/email providers is the
              next integration step.
            </CardDescription>
          </div>
          <Badge variant="info" className="uppercase tracking-wide">
            Collections
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={form.handleSubmit((v) => save.mutate(v))}
          className="flex flex-col gap-vt-4"
        >
          <label className="flex items-center gap-vt-2 text-vt-13 text-vt-text">
            <input
              type="checkbox"
              {...form.register('auto_reminder_enabled')}
              className="h-4 w-4"
            />
            Auto-send reminders to defaulters
          </label>
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="cp-offsets">Day offsets (CSV)</Label>
              <Input
                id="cp-offsets"
                placeholder="3,7,14,30"
                maxLength={60}
                {...form.register('reminder_day_offsets')}
              />
              {form.formState.errors.reminder_day_offsets ? (
                <p className="text-vt-12 text-vt-alert">
                  {form.formState.errors.reminder_day_offsets.message}
                </p>
              ) : (
                <p className="text-vt-11 text-vt-text-muted">
                  Days after a bill's due date when a reminder fires. Leave empty to disable.
                </p>
              )}
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="cp-channel">Default channel</Label>
              <Select id="cp-channel" {...form.register('default_channel')}>
                <option value="email">Email</option>
                <option value="sms">SMS</option>
                <option value="whatsapp">WhatsApp</option>
                <option value="app_notification">App notification</option>
              </Select>
              <p className="text-vt-11 text-vt-text-muted">
                Used by the bulk reminder dialog as the pre-selected channel.
              </p>
            </div>
          </div>
          <div className="flex items-center justify-end border-t border-vt-border pt-vt-3">
            <Button
              type="submit"
              loading={save.isPending}
              loadingText="Saving…"
              disabled={!form.formState.isDirty}
            >
              Save collection policy
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
